import { format, parseISO } from "date-fns";
import {
  Mission,
  MissionCondition,
  MissionReward,
  MissionEstablishment,
} from "../types/mission.type";
import { backendRewardTypeToFrontend } from "../utils/missionMapper";

type IncludedIndex = Map<string, any>;

const makeIncludedKey = (value?: { type?: string; id?: string }) =>
  value?.type && value?.id ? `${value.type}:${value.id}` : undefined;

const buildIncludedIndex = (included?: any[] | IncludedIndex) => {
  if (!included) return undefined;
  if (included instanceof Map) return included;
  const map: IncludedIndex = new Map();
  included.forEach((entry) => {
    if (!entry) return;
    if (entry.id) {
      map.set(String(entry.id), entry);
    }
    const key = makeIncludedKey(entry);
    if (key) {
      map.set(key, entry);
    }
  });
  return map;
};

const unwrapResource = (value: any, includedIndex?: IncludedIndex): any => {
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value
      .map((entry) => unwrapResource(entry, includedIndex))
      .filter(Boolean);
  }

  if (value.data !== undefined) {
    return unwrapResource(value.data, includedIndex);
  }

  if (includedIndex && value.type && value.id && !value.attributes) {
    const key = makeIncludedKey(value);
    const matched =
      (key && includedIndex.get(key)) || includedIndex.get(String(value.id));
    if (matched) {
      return unwrapResource(matched, includedIndex);
    }
  }

  if (value.attributes) {
    const base: Record<string, any> = {
      id: value.id ?? value.attributes?.id,
      ...value.attributes,
    };

    if (value.relationships && includedIndex) {
      Object.entries(value.relationships).forEach(
        ([relationName, relationValue]) => {
          const relationData = (relationValue as any)?.data;
          if (!relationData) return;

          if (Array.isArray(relationData)) {
            base[relationName] = relationData
              .map((relationItem) =>
                unwrapResource(relationItem, includedIndex),
              )
              .filter(Boolean);
          } else {
            base[relationName] = unwrapResource(relationData, includedIndex);
          }
        },
      );
    }

    return base;
  }

  return value;
};

const toDateString = (value?: string | null) => {
  if (!value) return "";
  try {
    return format(parseISO(value), "dd/MM/yyyy");
  } catch {
    return value;
  }
};

const extractDisplayName = (source?: any) => {
  if (!source) return undefined;
  return (
    source.display_name ||
    source.displayName ||
    source.name ||
    source.title ||
    source.fantasy_name ||
    source.official_name ||
    source.description ||
    source.general_information?.fantasy_name ||
    source.generalInformation?.fantasy_name
  );
};

const normalizeEstablishment = (
  raw?: any,
  includedIndex?: IncludedIndex,
): MissionEstablishment | null => {
  if (!raw) return null;
  const payload = unwrapResource(raw, includedIndex);
  if (!payload) return null;

  const displayName = extractDisplayName(payload) || "Estabelecimento";

  return {
    id: String(payload.id ?? payload.uuid ?? payload.geo_id ?? ""),
    display_name: displayName,
  };
};

const normalizeCondition = (
  raw: any,
  includedIndex?: IncludedIndex,
): MissionCondition => {
  const payload = unwrapResource(raw, includedIndex) || {};

  const properties = (payload.properties || {}) as Record<string, any>;
  const providerKlass = payload.provider_klass || payload.providerKlass || "";

  const rawLabel =
    payload.provider_label ||
    payload.providerLabel ||
    properties.label ||
    providerKlass?.split("::").pop() ||
    "Requisito";

  const typeLabel = String(rawLabel).trim();

  const quantity =
    payload.quantity ?? payload.value_label ?? payload.valueLabel;

  const rawValue =
    payload.value_label ||
    payload.valueLabel ||
    properties.value_label ||
    properties.value ||
    (quantity !== undefined && quantity !== null
      ? String(quantity)
      : undefined) ||
    payload.metadata?.value_label ||
    payload.metadata?.label ||
    "--";

  const valueLabel = String(rawValue);

  const rawGroup =
    payload.condition_group_label ||
    payload.group_label ||
    properties.condition_group_label ||
    "general";

  const conditionGroup = String(rawGroup);

  return {
    id: String(
      payload.id ?? payload.uuid ?? createTemporaryId("mission-field"),
    ),
    condition_group_label: conditionGroup,
    type_label: typeLabel,
    value_label: valueLabel,
    provider_klass: providerKlass,
    properties,
  };
};

const createTemporaryId = (prefix: string) =>
  `${prefix}-${Math.random().toString(36).slice(2)}`;

const normalizeReward = (
  raw: any,
  includedIndex?: IncludedIndex,
): MissionReward => {
  const payload = unwrapResource(raw, includedIndex) || {};

  const rewardableSource =
    payload.rewardable ||
    payload.reward ||
    payload.reward_resource ||
    payload.product ||
    payload.tmais_product;

  const rewardable =
    unwrapResource(rewardableSource, includedIndex) || rewardableSource;
  let rewardType = backendRewardTypeToFrontend(payload.reward_type);

  const resolveId = (value?: any) => {
    if (value === undefined || value === null) return undefined;
    return String(value);
  };

  const rawRewardId =
    payload.reward_id ??
    payload.rewardable_id ??
    payload.product_id ??
    rewardable?.id;

  const productId =
    rewardType === "product" && rawRewardId
      ? resolveId(rawRewardId)
      : undefined;
  const normalizedRewardId =
    rewardType === "product" ? productId : resolveId(rawRewardId);

  const rewardLabel =
    payload.reward_label ||
    extractDisplayName(rewardable) ||
    rewardable?.product_name ||
    rewardable?.label;

  if (
    rewardType === "product" &&
    (payload.reward_type === null || payload.reward_type === undefined) &&
    !productId &&
    !normalizedRewardId &&
    !rewardable
  ) {
    rewardType = "points";
  }

  const missionRewardRelationSource =
    payload.mission_reward ||
    payload.missionReward ||
    payload.mission_reward_relation ||
    payload.missionRewardRelation;
  const missionRewardRelation = unwrapResource(
    missionRewardRelationSource,
    includedIndex,
  );

  const missionRewardId =
    resolveId(payload.id) ??
    resolveId(payload.mission_reward_id) ??
    resolveId(payload.pivot_id) ??
    resolveId(payload.uuid) ??
    resolveId(missionRewardRelation?.id) ??
    resolveId(missionRewardRelation?.mission_reward_id) ??
    resolveId(missionRewardRelation?.pivot_id);

  const relationId = missionRewardId ?? createTemporaryId("mission-field");

  return {
    id: relationId,
    mission_reward_id: missionRewardId ?? null,
    pivot_id: missionRewardId ?? null,
    reward_type: rewardType,
    quantity: Number(payload.quantity ?? 1),
    reward_id: normalizedRewardId ?? null,
    product_id: productId ?? null,
    reward_label: rewardLabel,
    product_name: rewardType === "product" ? rewardLabel : undefined,
    reward: payload.reward
  };
};

const ensureArray = (value?: any) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.data)) return value.data;
  if (value.data) return [value.data];
  return [value];
};

const resolveMinimumLoyalty = (
  value: any,
): Mission["minimum_loyalty"] | undefined => {
  if (!value) return undefined;
  if (typeof value === "string") return value as Mission["minimum_loyalty"];
  if (typeof value === "number")
    return String(value) as Mission["minimum_loyalty"];
  if (typeof value === "object") {
    return (
      value.value ||
      value.slug ||
      value.code ||
      value.key ||
      value.level ||
      value.name
    );
  }
  return undefined;
};

const resolveStatus = (value?: string | null): Mission["status"] => {
  if (!value) return "disabled";
  const normalized = String(value).toLowerCase();
  if (normalized === "active") return "active";
  return "disabled";
};

export class MissionsTransformer {
  static fromApi(item: any, included?: any[] | IncludedIndex): Mission {
    const includedIndex = buildIncludedIndex(included);
    const payload = unwrapResource(item, includedIndex) || {};

    const establishment =
      payload.establishment ||
      payload.establishment_company_branch ||
      (payload.establishment_company_branch_id
        ? {
            id: payload.establishment_company_branch_id,
            display_name:
              payload.establishment_company_branch_display_name ||
              payload.establishment_display_name ||
              payload.establishment_name,
          }
        : undefined);

    const allowedEstablishments =
      payload.allowed_establishments ||
      payload.allowed_establishment_company_branches ||
      [];

    const allowedEstablishmentIds =
      payload.allowed_establishment_company_branch_ids ||
      payload.allowed_establishment_ids ||
      payload.allowed_establishments_ids;

    const requirementsSource =
      payload.requirements ||
      payload.requirements_attributes ||
      payload.requirementsAttributes ||
      payload.conditions ||
      payload.mission_requirements ||
      payload.missionRequirements;

    const rewardsSource =
      payload.mission_rewards ||
      payload.missionRewards ||
      payload.rewards ||
      payload.rewards_attributes ||
      payload.rewardsAttributes;

    const requirements = ensureArray(requirementsSource).map(
      (requirement: any) => normalizeCondition(requirement, includedIndex),
    );
    const rewards = ensureArray(rewardsSource).map((reward: any) =>
      normalizeReward(reward, includedIndex),
    );

    const maxPerUser =
      payload.maximum_completions_per_user ?? payload.max_times;

    const minimumLoyaltyRaw =
      resolveMinimumLoyalty(payload.minimum_loyalty) ||
      resolveMinimumLoyalty(payload.minimum_loyalty_level) ||
      resolveMinimumLoyalty(payload.minimumLoyalty) ||
      resolveMinimumLoyalty(payload.minimumLoyaltyLevel) ||
      resolveMinimumLoyalty(payload.loyalty_requirement) ||
      resolveMinimumLoyalty(payload.loyaltyRequirement) ||
      "indifferent";

    const minimumLoyalty = String(minimumLoyaltyRaw || "indifferent")
      .trim()
      .toLowerCase() as Mission["minimum_loyalty"];

    return {
      id: String(payload.id ?? item?.id ?? ""),
      mission_type: (payload.mission_type ||
        (establishment ? "establishment" : "tcard")) as Mission["mission_type"],
      establishment: normalizeEstablishment(establishment, includedIndex),
      status: resolveStatus(payload.status),
      name: payload.display_name || payload.name || "",
      display_name: payload.display_name || payload.name || "",
      description: payload.description || "",
      allow_multiple_times: Boolean(maxPerUser && Number(maxPerUser) > 1),
      max_times: maxPerUser ? Number(maxPerUser) : null,
      minimum_loyalty: minimumLoyalty,
      start_date: payload.start_date || toDateString(payload.available_from),
      end_date: payload.end_date || toDateString(payload.available_until),
      restrict_establishments: Boolean(payload.restrict_establishments),
      allowed_establishments: (() => {
        const resolved = ensureArray(allowedEstablishments)
          .map((allowed: any) => normalizeEstablishment(allowed, includedIndex))
          .filter(Boolean) as MissionEstablishment[];
        if (resolved.length > 0) return resolved;
        if (Array.isArray(allowedEstablishmentIds)) {
          return allowedEstablishmentIds
            .map((id: any) => normalizeEstablishment({ id }, includedIndex))
            .filter(Boolean) as MissionEstablishment[];
        }
        return resolved;
      })(),
      conditions: requirements,
      rewards,
    };
  }

  static listFromApi(
    items: any[] = [],
    included?: any[] | IncludedIndex,
  ): Mission[] {
    const includedIndex = buildIncludedIndex(included);
    return items.map((item) =>
      MissionsTransformer.fromApi(item, includedIndex),
    );
  }
}
